import type { UserCourseLesson } from "api/schemas/UserCourse";
import type { ExerciseListItem } from "api/services/content/schemas/exercises";
import type {
  PatientTreatmentSchedule,
  PatientTreatmentScheduleExercise,
  PostTreatmentSchedulePayload,
  TreatmentScheduleExercisePayload,
  TreatmentScheduleTemplate,
} from "api/services/treatment/schemas/Treatment";

import type { TimelineActivity, TimelineWeek } from "./types";

export const DEFAULT_SETS = 1;
export const DEFAULT_REPS = "10";

function isTreatmentScheduleTemplate(
  schedule: PatientTreatmentSchedule | TreatmentScheduleTemplate
): schedule is TreatmentScheduleTemplate {
  return "group" in schedule;
}

export function getDefaultValues(
  treatmentSchedule?: PatientTreatmentSchedule | TreatmentScheduleTemplate
): PostTreatmentSchedulePayload {
  const isTemplate = treatmentSchedule && isTreatmentScheduleTemplate(treatmentSchedule);

  return {
    duration_weeks: treatmentSchedule?.duration_weeks || null,
    exercises: treatmentSchedule?.exercises || [],
    exercises_per_day: treatmentSchedule?.exercises_per_day || 3,
    group: isTemplate ? treatmentSchedule.group : null,
    health_form_type: treatmentSchedule?.health_form_type || "generic",
    includes_functional_test: !!treatmentSchedule?.includes_functional_test,
    key: isTemplate ? treatmentSchedule.key : null,
    lessons: treatmentSchedule?.lessons || [],
    name: isTemplate ? treatmentSchedule.name : null,
  };
}

interface ConvertExerciseToTimelineActivityArgs {
  exercise: ExerciseListItem;
  level_locked: boolean;
  pinned: boolean;
  recommended_level: number;
  weekNumber: number;
}

export function convertExerciseToTimelineActivity({
  exercise,
  level_locked,
  pinned,
  recommended_level,
  weekNumber,
}: ConvertExerciseToTimelineActivityArgs): TimelineActivity {
  return {
    duration_icon: exercise.levels?.[0]?.duration_icon,
    duration_unit: exercise.levels?.[0]?.duration_unit,
    exercise_id: exercise.exercise_id,
    id: `exercise-${exercise.exercise_id}-${weekNumber}-${Date.now()}`,
    image_url: exercise.levels?.[0]?.image_url || null,
    level_locked,
    level: recommended_level,
    levels: exercise.levels.map(level => ({
      image_url: level.image_url || null,
      level: level.level,
      title: level.title,
      variation_title: level.variation_title || null,
      video_thumbnail_file_url: level.video_thumbnail_file_url || null,
    })),
    max_level: exercise.number_of_levels || 1,
    pinned,
    recommended_level,
    recommended_reps: null,
    recommended_sets: null,
    reps: exercise.levels?.[0]?.reps || DEFAULT_REPS,
    required: false,
    sets: exercise.levels?.[0]?.sets || DEFAULT_SETS,
    title: exercise.levels?.[0]?.title || `Exercise ${exercise.exercise_id}`,
    type: "exercise",
    variation_title: exercise.levels?.[0]?.variation_title || null,
    video_thumbnail_file_url: exercise.levels?.[0]?.video_thumbnail_file_url || null,
    weekNumber,
  };
}

interface ConvertScheduleExercisesToTimelineActivitiesArgs {
  exercise: PatientTreatmentScheduleExercise;
  index: number;
  weekNumber: number;
}

export function convertScheduleExercisesToTimelineActivities({
  exercise,
  index,
  weekNumber,
}: ConvertScheduleExercisesToTimelineActivitiesArgs): TimelineActivity {
  return {
    days: exercise.days ?? [],
    duration_icon: exercise.duration_icon,
    duration_unit: exercise.duration_unit,
    exercise_id: exercise.exercise_id,
    id: `exercise-${exercise.exercise_id}-${weekNumber}-${index}`,
    image_url: exercise.image_url,
    level_locked: exercise.level_locked,
    level: exercise.recommended_level,
    levels: [
      {
        image_url: exercise.image_url,
        level: exercise.recommended_level,
        title: exercise.title,
        variation_title: exercise.variation_title,
        video_thumbnail_file_url: exercise.video_thumbnail_file_url,
      },
    ],
    max_level: exercise.max_level,
    pinned: exercise.pinned,
    recommended_level: exercise.recommended_level,
    recommended_reps: exercise.recommended_reps,
    recommended_sets: exercise.recommended_sets,
    reps: exercise.reps || DEFAULT_REPS,
    required: false,
    sets: exercise.sets || DEFAULT_SETS,
    title: exercise.title,
    type: "exercise",
    variation_title: exercise.variation_title,
    video_thumbnail_file_url: exercise.video_thumbnail_file_url,
    weekNumber,
  };
}

export function convertWeeksToExercises(weeks: TimelineWeek[]): TreatmentScheduleExercisePayload[] {
  return Object.values(
    weeks
      .flatMap(week => week.activities.filter(activity => activity.type === "exercise"))
      .reduce((acc, item) => {
        const { exercise_id } = item;
        if (!acc[exercise_id]) {
          acc[exercise_id] = {
            days: item.days || [],
            duration_icon: item.duration_icon ?? null,
            duration_unit: item.duration_unit ?? null,
            exercise_id: item.exercise_id,
            level_locked: item.level_locked || false,
            pinned: item.pinned || false,
            recommended_level: item.level,
            recommended_reps: item.recommended_reps,
            recommended_sets: item.recommended_sets,
            weeks: [],
          };
        }
        acc[exercise_id].weeks.push(item.weekNumber);
        return acc;
      }, {} as Record<number, TreatmentScheduleExercisePayload>)
  );
}

export function convertWeeksToScheduleExercises(weeks: TimelineWeek[]): PatientTreatmentScheduleExercise[] {
  return Object.values(
    weeks
      .flatMap(week => week.activities.filter(activity => activity.type === "exercise"))
      .reduce((acc, item) => {
        const { exercise_id } = item;
        if (!acc[exercise_id]) {
          acc[exercise_id] = {
            days: item.days ?? null,
            duration_icon: item.duration_icon || "repetitions",
            duration_unit: item.duration_unit || "repetitions",
            exercise_id: item.exercise_id,
            image_url: item.image_url,
            level_locked: item.level_locked ?? false,
            max_level: item.max_level ?? 1,
            pinned: item.pinned ?? false,
            recommended_level: item.level,
            recommended_reps: item.recommended_reps || null,
            recommended_sets: item.recommended_sets || null,
            reps: item.reps,
            sets: item.sets,
            title: item.title,
            variation_title: item.variation_title,
            video_thumbnail_file_url: item.video_thumbnail_file_url,
            weeks: [],
          };
        }
        acc[exercise_id].weeks.push(item.weekNumber);
        return acc;
      }, {} as Record<number, PatientTreatmentScheduleExercise>)
  );
}

interface CreatePayloadArgs extends Omit<PostTreatmentSchedulePayload, "exercises" | "lessons"> {
  lessons: UserCourseLesson[];
  weeks: TimelineWeek[];
}

export function createPayload({
  duration_weeks,
  weeks,
  exercises_per_day,
  group,
  health_form_type,
  includes_functional_test,
  key,
  lessons,
  name,
}: CreatePayloadArgs): PostTreatmentSchedulePayload {
  return {
    duration_weeks,
    exercises: convertWeeksToExercises(weeks),
    exercises_per_day,
    group,
    health_form_type,
    includes_functional_test,
    key,
    lessons: lessons.map((lesson, index) => ({
      position: index + 1,
      text_lesson_id: lesson.id,
    })),
    name,
  };
}

interface HealthFormMatchesTreatmentArgs {
  healthFormType: string;
  patientAilment?: string;
  patientPrimaryJoint?: string;
}

export function healthFormMatchesTreatment({
  healthFormType,
  patientAilment,
  patientPrimaryJoint,
}: HealthFormMatchesTreatmentArgs): boolean {
  // if theres no patient patient to compare to, assume match (ie creting a new templated wihout a patient context)
  if (!patientAilment && !patientPrimaryJoint) return true;

  if (patientPrimaryJoint) {
    const jointLower = patientPrimaryJoint.toLowerCase();
    const formTypeLower = healthFormType.toLowerCase();

    if (jointLower.includes("knee") && formTypeLower.includes("knee")) return true;
    if (jointLower.includes("hip") && formTypeLower.includes("hip")) return true;
    if (jointLower.includes("shoulder") && formTypeLower.includes("shoulder")) return true;
    if (jointLower.includes("back") && formTypeLower.includes("back")) return true;
    if (jointLower.includes("neck") && formTypeLower.includes("neck")) return true;
    if (jointLower.includes("hand") && formTypeLower.includes("hand")) return true;
    if (formTypeLower === "generic") return true; // Generic always matches for joint-pain ailment.
  }
  if (patientAilment === "osteoporosis" && healthFormType === "osteoporosis") return true;
  if (patientAilment === "fall_prevention" && healthFormType === "fall_prevention") return true;
  return false;
}
